import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { hasPermission, useAuth } from "@/lib/auth";
import { governanceErrorMessage } from "@/lib/governance-error";
import { revisionCapabilities, revisionHasUnsavedChanges, revisionSaveBody, type GovernanceRevision } from "@/lib/governance-revision";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function RevisionEditor({ id, onClose, onPublished }: {
  id: string;
  onClose: () => void;
  onPublished: (revision: GovernanceRevision) => void;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [revision, setRevision] = useState<GovernanceRevision>();
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState<"close" | "cancel" | "publish">();
  const initialized = useRef(false);
  const locked = useRef(false);
  const query = useQuery<GovernanceRevision>({
    queryKey: ["content-governance-revision", id],
    queryFn: async () => (await apiRequest("GET", `/api/content-governance/revisions/${id}`)).json(),
    refetchOnWindowFocus: false,
  });
  // Initialize once per keyed editor. Background refetches never replace typed text.
  useEffect(() => {
    if (query.data && !initialized.current) {
      initialized.current = true;
      setRevision(query.data); setName(query.data.name); setContent(query.data.content);
    }
  }, [query.data]);
  const capabilities = revision ? revisionCapabilities(revision, user?.id, {
    manage: hasPermission(user, "knowledge.manage"),
    review: hasPermission(user, "content.review"),
    publish: hasPermission(user, "content.publish"),
  }) : undefined;
  const unsaved = !!revision && revisionHasUnsavedChanges(revision, name, content);
  const stale = !!revision && !!query.data && query.data.stamp !== revision.stamp;
  function requestClose() {
    if (locked.current) return;
    if (unsaved) setConfirmation("close");
    else onClose();
  }
  async function perform(action: "save" | "submit" | "approve" | "reject" | "cancel" | "publish") {
    if (!revision || locked.current) return;
    if ((action === "approve" || action === "reject") && !comment.trim()) return;
    locked.current = true; setBusy(true); setError(""); setMessage("");
    try {
      let updated = revision;
      if (action === "save" || action === "submit") {
        updated = await (await apiRequest("PUT", `/api/content-governance/revisions/${id}`,
          revisionSaveBody(name, content, revision.stamp))).json();
        // Keep the saved stamp even when the following submit fails.
        setRevision(updated);
        qc.setQueryData(["content-governance-revision", id], updated);
      }
      if (action !== "save") {
        updated = await (await apiRequest("POST", `/api/content-governance/revisions/${id}/${action}`, {
          stamp: updated.stamp,
          ...((action === "approve" || action === "reject") ? { comment: comment.trim() } : {}),
        })).json();
        setRevision(updated);
        qc.setQueryData(["content-governance-revision", id], updated);
      }
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["content-governance"] }),
        qc.invalidateQueries({ queryKey: ["content-governance-summary"] }),
        qc.invalidateQueries({ queryKey: ["content-governance-revisions"] }),
        qc.invalidateQueries({ queryKey: ["content-governance-revision", id] }),
        qc.invalidateQueries({ queryKey: ["content-governance-versions", updated.knowledgeSourceId] }),
        qc.invalidateQueries({ queryKey: ["/api/chatbots", updated.chatbotId, "knowledge"] }),
      ]);
      setConfirmation(undefined); setComment("");
      if (action === "publish") onPublished(updated);
      else if (action === "cancel") onClose();
      else setMessage(action === "save" ? "Revision saved. Published content is unchanged."
        : action === "submit" ? "Submitted for review. Approval and publication are still required."
        : action === "approve" ? "Revision approved. Publication is still required." : "Revision rejected. Feedback is available to the creator.");
    } catch (err) {
      setError(governanceErrorMessage(err));
      // Do not reset local inputs or refresh the stamp on a conflict.
    } finally { locked.current = false; setBusy(false); }
  }
  return <Dialog open onOpenChange={open => { if (!open) requestClose(); }}>
    <DialogContent className="sm:max-w-2xl max-h-[90dvh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Content revision</DialogTitle>
        <DialogDescription>Published content stays unchanged until this revision is approved and published on the original source.</DialogDescription>
      </DialogHeader>
      {query.isLoading && <div role="status" aria-label="Loading revision" className="space-y-3 animate-pulse"><div className="h-8 rounded bg-muted" /><div className="h-40 rounded bg-muted" /></div>}
      {query.error && <div role="alert" className="space-y-2 text-sm text-destructive">
        <p>{governanceErrorMessage(query.error)}</p>
        <Button variant="outline" disabled={busy || query.isFetching} onClick={() => void query.refetch()}>Retry loading revision</Button>
      </div>}
      {revision && <>
        <dl className="rounded-lg border bg-muted/30 p-3 text-xs space-y-1 break-words">
          <div><dt className="inline font-medium">Source name: </dt><dd className="inline">{revision.name}</dd></div>
          <div><dt className="inline font-medium">Original source: </dt><dd className="inline">{revision.knowledgeSourceId}</dd></div>
          <div><dt className="inline font-medium">Current published version: </dt><dd className="inline">v{revision.currentPublishedVersion}</dd></div>
          <div><dt className="inline font-medium">{revision.restoredFromVersion !== null ? "Restored from " : "Based on "}</dt><dd className="inline">v{revision.restoredFromVersion ?? revision.baseVersion}</dd></div>
          {revision.restoredFromVersionId && <div><dt className="inline font-medium">Snapshot ID: </dt><dd className="inline">{revision.restoredFromVersionId}</dd></div>}
          <div><dt className="inline font-medium">Created by: </dt><dd className="inline">{revision.createdByUserId} · {new Date(revision.createdAt).toLocaleString()}</dd></div>
          <div><dt className="inline font-medium">Status: </dt><dd className="inline capitalize">{revision.status}</dd></div>
        </dl>
        {revision.reviewComments && <p className="rounded border p-3 text-sm"><strong>Review feedback: </strong>{revision.reviewComments}</p>}
        {stale && <p role="alert" className="text-sm text-destructive">This revision changed on the server. Your text has not been replaced. Copy unsaved changes before closing and reopening to load the latest revision.</p>}
        <label htmlFor="revision-name" className="text-sm font-medium">Name</label>
        <Input id="revision-name" maxLength={200} value={name} readOnly={!capabilities?.edit} disabled={busy} onChange={event => setName(event.target.value)} />
        <label htmlFor="revision-content" className="text-sm font-medium">Content</label>
        <Textarea id="revision-content" rows={12} maxLength={200000} value={content} readOnly={!capabilities?.edit} disabled={busy} onChange={event => setContent(event.target.value)} />
        {capabilities?.edit && <p role="status" className="text-xs text-muted-foreground">{unsaved ? "Unsaved changes. Save before leaving." : "No unsaved changes."}</p>}
        {(capabilities?.approve || capabilities?.reject) && <>
          <label htmlFor="revision-comment" className="text-sm font-medium">Review comment (required)</label>
          <Textarea id="revision-comment" value={comment} disabled={busy} onChange={event => setComment(event.target.value)} rows={3} />
        </>}
        <div className="flex flex-wrap gap-2">
          {capabilities?.edit && <>
            <Button disabled={busy || stale || !name.trim() || !content.trim()} onClick={() => void perform("save")}>Save revision</Button>
            <Button disabled={busy || stale || !name.trim() || !content.trim()} onClick={() => void perform("submit")}>Save and submit for review</Button>
          </>}
          {capabilities?.approve && <Button disabled={busy || stale || !comment.trim()} onClick={() => void perform("approve")}>Approve revision</Button>}
          {capabilities?.reject && <Button variant="destructive" disabled={busy || stale || !comment.trim()} onClick={() => void perform("reject")}>Reject revision</Button>}
          {capabilities?.publish && <Button disabled={busy || stale} onClick={() => setConfirmation("publish")}>Publish revision</Button>}
          {capabilities?.cancel && <Button variant="outline" disabled={busy || stale} onClick={() => setConfirmation("cancel")}>Cancel revision</Button>}
        </div>
      </>}
      {error && <div role="alert" className="text-sm text-destructive"><p>{error}</p><p>Your text has been retained. For a stale revision, copy unsaved changes before reopening; do not retry with an outdated stamp.</p></div>}
      {message && <p role="status" className="text-sm">{message}</p>}
      {busy && <p role="status" className="text-sm text-muted-foreground">Updating revision…</p>}
      {confirmation && <div role="group" aria-label="Confirm revision action" className="border rounded p-3 space-y-3 text-sm">
        <p>{confirmation === "close" ? "Discard unsaved changes and close?"
          : confirmation === "cancel" ? "Cancel this revision? Unsaved changes will be discarded. Published content and history stay unchanged."
          : `Publish this revision on the original source as v${(revision?.currentPublishedVersion ?? 0) + 1}? This replaces its current published content, not its history.`}</p>
        <div className="flex flex-wrap gap-2">
          <Button disabled={busy} onClick={() => {
            if (confirmation === "close") onClose();
            else if (confirmation === "cancel" || confirmation === "publish") void perform(confirmation);
          }}>Confirm {confirmation}</Button>
          <Button variant="outline" disabled={busy} onClick={() => setConfirmation(undefined)}>Keep editing</Button>
        </div>
      </div>}
      <Button variant="outline" disabled={busy} onClick={requestClose}>Close</Button>
    </DialogContent>
  </Dialog>;
}
